import { EventedArray } from '../Dependency/EventedArray';
import { getFeatureFlag } from './FeatureFlags';

declare global {
  interface Window {
    AbsoluteOrientationSensor: any;
  }
}

// Last position or error from either source (see watchPosition)
let lastDeliveryAt = 0;
let watchId: number | null = null;
let watchPositionInterval: any = null;
let orientationSensor: any = null;
let currentOrientation: number | null = null;

// Silence after which the watchdog asks for a position itself
const WATCHDOG_IDLE_MS = 3000;
const WATCHDOG_TICK_MS = 1000;

// Default number of positions to keep in the history
export const POSITIONS_PIPELINE_LENGTH = 10;
export const watchHistory: EventedArray = new EventedArray(POSITIONS_PIPELINE_LENGTH);

export const geolocOptions = {
  enableHighAccuracy: true,
  timeout: 3000,
  maximumAge: 1000,
};

export interface TWPositionData {
  error?: string;
  lat?: number;
  lng?: number;
  speed?: number;
  accuracy?: number;
  altitude?: number;
  altitudeAccuracy?: number;
  orientation?: number;
  timestamp?: number;
}

export const defaultMinimumAccuracy: number = 10;
export const minimumAccuracy: number = parseInt(getFeatureFlag('minimumAccuracy')) || 10;

// Monitor only new OS native position pushes (delivery, position or error)
// - If no OS push after WATCHDOG_IDLE_MS the "OS Sleeping" mode starts with WATCHDOG_TICK_MS callbacks, until it's interrupted by a new push
// - Watchdog is needed because a still device can stay silent for long, leaving accuracy value on screen frozen
export async function watchPosition(success?: Function, reject?: Function) {
  if (!navigator.geolocation) {
    alert('geolocation not supported');
    return;
  }

  // The same fix can come twice: the OS watch repeats the last reading while the device does not move,
  // and the watchdog asks for a position with `maximumAge: 1000`, which the browser may answer with the
  // cached one. A delivery is timestamped when it is *received*, so its timestamp cannot tell two of
  // them apart - the position can, and the pipeline has to hold 10 distinct fixes rather than copies of
  // the same one.
  const isSameFix = (current: TWPositionData, next: TWPositionData) => current.lat === next.lat
    && current.lng === next.lng
    && current.accuracy === next.accuracy
    && current.altitude === next.altitude
    && current.speed === next.speed;

  // Both sources (OS native position pushes + Watchdog timer) report through here:
  // any push (delivery / position / error) means the OS native position is answering thus Watchdog is not needed
  // An unusable delivery - an error, or a position without lat/lng/accuracy - is the only thing that
  // describes the current fix, so the history starts over with it: keeping the older valid positions
  // next to it would suggest they are still what the fix is. A usable position is appended, unless it is
  // the very same fix as one already in the history.
  const addToHistory = (data: TWPositionData) => {
    if (!isValidCoordinates(data)) {
      watchHistory.values = [];
    }

    lastDeliveryAt = Date.now();

    if (!watchHistory.values.some((position: TWPositionData) => isSameFix(position, data))) {
      watchHistory.add(data);
    }
  };

  const onPosition = (results: GeolocationPosition) => {
    const data: TWPositionData = getGpsDataHistoryObject(results);
    addToHistory(data);
    success && success(data);
  };

  const onPositionError = (error: GeolocationPositionError) => {
    const errorData: TWPositionData = getGpsDataHistoryObject(undefined, error);
    addToHistory(errorData);
    reject && reject(error);
  };

  if (!watchId) {
    watchId = navigator.geolocation.watchPosition(onPosition, onPositionError, geolocOptions);
  }

  if (!watchPositionInterval) {
    watchPositionInterval = setInterval(() => {
      // A still device can stay silent for long: ask for one position only once nothing has been
      // delivered for a while, so the accuracy on screen can never freeze.
      if ((Date.now() - lastDeliveryAt) < WATCHDOG_IDLE_MS) {
        return;
      }
      navigator.geolocation.getCurrentPosition(onPosition, onPositionError, geolocOptions);
    }, WATCHDOG_TICK_MS);
  }
}

export function hasSufficientAccuracy(accuracy?: number): boolean {
  try {
    if (!accuracy) {
      return false
    }
    return Math.floor(accuracy) <= minimumAccuracy;
  } catch (err) {
    return false;
  }
}

export function isValidCoordinates(coords: TWPositionData): boolean {
  if (!coords) {
    return false;
  }

  // 0 is a valid coordinate (exactly on the equator or the prime meridian),
  // while undefined, NaN and Infinity are not usable positions.
  return Number.isFinite(coords.accuracy)
    && Number.isFinite(coords.lat)
    && Number.isFinite(coords.lng);
}

/**
 * Calculate the distance between two GPS coordinates using the Haversine formula.
 * @param lat1 Latitude of first point
 * @param lng1 Longitude of first point
 * @param lat2 Latitude of second point
 * @param lng2 Longitude of second point
 * @returns Distance in meters
 */
export function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

function nullToUndefined(value?: number | null) {
  return value === null || value === undefined ? undefined : value;
}

/**
 * The orientation the device has right now, in degrees, or undefined when no sensor reading received
 */
export function readOrientation(): number | undefined {
  return nullToUndefined(currentOrientation);
}

export function getGpsDataHistoryObject(coords?: GeolocationPosition, error?: GeolocationPositionError): TWPositionData {
  const result = {
    error: error?.message,
    lat: coords?.coords.latitude,
    lng: coords?.coords.longitude,
    // Only a missing reading becomes undefined. These used to be truthiness
    // checks, which discarded every legitimate zero: a planter standing still
    // reports speed 0 and a tree planted at sea level reports altitude 0, and
    // both were dropped from the payload as if the device had no GPS.
    speed: nullToUndefined(coords?.coords.speed),
    accuracy: coords?.coords.accuracy,
    altitude: nullToUndefined(coords?.coords.altitude),
    altitudeAccuracy: nullToUndefined(coords?.coords.altitudeAccuracy),
    orientation: currentOrientation !== null ? currentOrientation : nullToUndefined(coords?.coords.heading),
    timestamp: new Date().getTime()
  };

  return result;
}

export async function initOrientationSensor(): Promise<boolean> {
  try {
    if ('AbsoluteOrientationSensor' in window) {
      orientationSensor = new (window as any).AbsoluteOrientationSensor({ frequency: 10 });
      
      orientationSensor.addEventListener('reading', () => {
        if (orientationSensor && orientationSensor.quaternion) {
          // Convert quaternion to heading (degrees)
          const [x, y, z, w] = orientationSensor.quaternion;
          // Calculate heading from quaternion
          const heading = Math.atan2(2 * (w * z + x * y), 1 - 2 * (y * y + z * z)) * (180 / Math.PI);
          currentOrientation = heading >= 0 ? heading : heading + 360;
        }
      });
      
      orientationSensor.addEventListener('error', (event: any) => {
        console.error('Orientation sensor error:', event);
        currentOrientation = null;
      });
      
      await orientationSensor.start();
      return true;
    } else {
      console.warn('AbsoluteOrientationSensor not supported');
      return false;
    }
  } catch (error) {
    console.error('Failed to initialize orientation sensor:', error);
    currentOrientation = null;
    return false;
  }
}

export function stopOrientationSensor(): void {
  if (orientationSensor) {
    orientationSensor.stop();
    orientationSensor = null;
    currentOrientation = null;
  }
}

export function getGeoJson(
  data: any,
  geoFeatureTemplate: Function,
  clusterProperties?: any
) {
  return {
    type: 'geojson',
    cluster: !!clusterProperties,
    clusterProperties: clusterProperties,
    data: {
      type: 'FeatureCollection',
      features: data.map((current: any) => geoFeatureTemplate(current))
    }
  };
}

/**
 * Splits the two positions the forms work with:
 *
 * - `displayCoordinates` is the live position, so it follows every fix, usable or
 *   not. It is what the user sees (GPS accuracy, "Waiting for GPS"), what enables
 *   the photo button and what the distance banner is based on, so opening the
 *   camera and closing it without taking a photo, or losing the fix, stays visible.
 * - `coordinates` is the position saved in the payload: the last fix with a
 *   sufficient accuracy, and, once the camera has been opened
 *   (`keepBestPosition`, set by TreeForm and MapPopupValidationForm), only a fix
 *   with a better accuracy can replace it.
 */
export function calculateGpsAccuracyState(
  currentCoords: TWPositionData | undefined,
  newCoords: TWPositionData | undefined,
  keepBestPosition: boolean,
  simulatedAccuracy?: number
) {
  // No fix means no accuracy: the old 9999 placeholder was compared to `minimumAccuracy`, so on any
  // environment whose threshold is above it "Waiting for GPS" counted as accurate enough
  const accuracy = simulatedAccuracy ?? newCoords?.accuracy ?? undefined;
  const currentCoordsAccuracy = currentCoords?.accuracy ?? 9999;
  const hasEnoughAccuracy = hasSufficientAccuracy(accuracy);

  const newState: any = { hasEnoughAccuracy };

  if (newCoords) {
    newState.displayCoordinates = newCoords;

    if (!currentCoords) {
      newState.coordinates = newCoords;
    } else if (hasEnoughAccuracy && (!keepBestPosition || (accuracy ?? Infinity) < currentCoordsAccuracy)) {
      newState.coordinates = newCoords;
    }
  }

  return newState;
}